import React, { useState } from 'react';
import { Card, Button, KPICard, FeaturePlaceholder, ConfirmDialog } from '@/components/ui';
import { 
  useConstructionBoqs, 
  useConstructionBoqDetail, 
  useConstructionBoqItems,
  useCreateBoqItem,
  useUpdateBoqItem,
  useDeleteBoqItem
} from '@/modules/construction/boq/hooks';
import { getStatusConfig, boqStatusMap } from '@/utils/status';
import { formatCurrency } from '@/utils/format';
import { BoqItemDesktopTable } from '@/modules/construction/boq/components/BoqItemDesktopTable';
import { BoqItemMobileList } from '@/modules/construction/boq/components/BoqItemMobileList';
import { BoqItemFormModal } from '@/modules/construction/boq/components/BoqItemFormModal';
import type { ConstructionBoqItem } from '@/modules/construction/boq/types/construction-boq.types';

interface ScopeTabProps {
  contractId: string;
  companyId: string;
}

export function ScopeTab({ contractId, companyId }: ScopeTabProps) {
  const { boqs, isLoading: isBoqsLoading } = useConstructionBoqs(companyId, { contractId });
  
  // For simplicity, we take the first BOQ associated with this contract
  const boqId = boqs[0]?.id;
  const { boq, isLoading: isDetailLoading } = useConstructionBoqDetail(boqId);
  const { items, stats, isLoading: isItemsLoading } = useConstructionBoqItems(boqId);
  
  const { mutateAsync: createItem } = useCreateBoqItem(boqId || '');
  const { mutateAsync: updateItem } = useUpdateBoqItem(boqId || '');
  const { mutateAsync: deleteItem } = useDeleteBoqItem(boqId || '');

  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState<ConstructionBoqItem | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);

  if (isBoqsLoading || isDetailLoading || isItemsLoading) {
    return <div className="p-12 text-center text-slate-500 text-sm">Đang tải dữ liệu BOQ...</div>;
  }

  if (!boqId) {
    return (
      <FeaturePlaceholder
        title="Chưa có BOQ cho hợp đồng này"
        description="Bạn cần khởi tạo bảng khối lượng (BOQ) để quản lý phạm vi thi công và chi phí chi tiết."
        primaryActionLabel="Khởi tạo BOQ"
        onPrimaryAction={() => {}}
      />
    );
  }

  const filteredItems = items.filter(item => 
    item.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.item_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSaveItem = async (data: Partial<ConstructionBoqItem>) => {
    if (editingItem) {
      await updateItem({ itemId: editingItem.id, updates: data });
    } else {
      await createItem(data);
    }
    setIsFormOpen(false);
    setEditingItem(null);
  };

  const handleDeleteItem = async () => {
    if (deletingItemId) {
      await deleteItem(deletingItemId);
      setDeletingItemId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard 
          title="Tổng giá trị BOQ" 
          value={formatCurrency(boq?.total_amount || 0)} 
          icon="ri-calculator-line" 
          variant="primary" 
        />
        <KPICard 
          title="Số lượng đầu mục" 
          value={stats?.itemCount || 0} 
          icon="ri-list-check-3" 
          variant="info" 
        />
        <KPICard 
          title="Trạng thái BOQ" 
          value={getStatusConfig(boq?.status_code || 'draft', boqStatusMap).label} 
          icon="ri-shield-check-line" 
          variant={getStatusConfig(boq?.status_code || 'draft', boqStatusMap).variant as any} 
        />
      </div>

      <Card className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h3 className="font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-survey-line text-os-primary" />
              Chi tiết Bảng khối lượng (BOQ)
            </h3>
            <p className="text-xs text-slate-500 mt-1">Danh sách các hạng mục thi công và vật tư trong phạm vi hợp đồng.</p>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text"
                placeholder="Tìm kiếm mã hoặc tên..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-os-primary/20 focus:border-os-primary transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="outline" icon="ri-download-2-line" size="sm" className="hidden md:flex">Xuất Excel</Button>
            <Button 
              variant="primary" 
              icon="ri-add-line" 
              size="sm" 
              onClick={() => {
                setEditingItem(null);
                setIsFormOpen(true);
              }}
            >
              Thêm hạng mục
            </Button>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block">
          <BoqItemDesktopTable 
            items={filteredItems} 
            onEdit={(item) => {
              setEditingItem(item);
              setIsFormOpen(true);
            }} 
            onDelete={setDeletingItemId} 
          />
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          <BoqItemMobileList 
            items={filteredItems} 
            onEdit={(item) => {
              setEditingItem(item);
              setIsFormOpen(true);
            }} 
            onDelete={setDeletingItemId} 
          />
        </div>

        {filteredItems.length > 0 && (
          <div className="mt-6 p-4 bg-slate-50 rounded-2xl border border-slate-100 flex justify-between items-center font-bold">
            <span className="text-sm text-slate-600">Tổng cộng giá trị BOQ</span>
            <span className="text-lg text-os-primary">{formatCurrency(boq?.total_amount || 0)}</span>
          </div>
        )}
      </Card>

      <BoqItemFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSaveItem}
        item={editingItem}
        boqId={boqId}
        projectId={boq?.project_id || ''}
        companyId={companyId}
      />

      <ConfirmDialog
        isOpen={!!deletingItemId}
        title="Xác nhận xóa hạng mục"
        message="Bạn có chắc chắn muốn xóa hạng mục này khỏi BOQ? Hành động này không thể hoàn tác."
        onConfirm={handleDeleteItem}
        onClose={() => setDeletingItemId(null)}
        isDestructive
      />
    </div>
  );
}
